import React, { useState, useEffect, useMemo } from 'react';

// --- CONFIG ---
// const API_BASE_URL = 'http://localhost:8081'; // Change to your backend URL
const API_BASE_URL = 'https://node-api-v4.filterpixel.com'; // Change to your backend URL
// const API_BASE_URL = 'https://node-api-dev.filterpixel.com'; // Change to your backend URL

// --- ICONS (Inlined) ---
const IconBase = ({ size = 24, className = "", children, ...props }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
    >
        {children}
    </svg>
);

const Check = (props) => (
    <IconBase {...props}>
        <polyline points="20 6 9 17 4 12" />
    </IconBase>
);

const X = (props) => (
    <IconBase {...props}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </IconBase>
);

const Info = (props) => (
    <IconBase {...props}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </IconBase>
);

// --- Parse URL Params ---
function useUrlParams() {
    return useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            email: params.get('email') || '',
            tier: params.get('tier') || '',
            payment: params.get('payment') || '', // 'success' | 'cancelled' | ''
            source: params.get('source') || '',    // 'app' when loaded from Electron
        };
    }, []);
}

// --- COMPONENTS ---

export const PricingModal = ({ currentPlan = "Suite" }) => {
    const { email, tier, payment, source } = useUrlParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [checkoutOpened, setCheckoutOpened] = useState(false);
    const [paymentDone, setPaymentDone] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState('');
    const [creditsBeforeCheckout, setCreditsBeforeCheckout] = useState(null);
    const [selectedPack, setSelectedPack] = useState('1'); // V2: always 1 shoot

    // Page is inside the Electron app when source=app is in the URL
    const isFromApp = source === 'app';

    // ── Browser-only: landing page (pricing UI only shows inside Electron) ──
    if (!isFromApp) {
        let icon, title, description;

        if (payment === 'success') {
            icon = (
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(47,161,140,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <Check size={32} style={{ color: '#2fa18c' }} />
                </div>
            );
            title = 'Payment Successful!';
            description = 'Your extra DeepCull shoot has been added to your account. You can close this tab and return to FilterPixel.';
        } else if (payment === 'cancelled') {
            icon = (
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(234,179,8,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <Info size={32} style={{ color: '#facc15' }} />
                </div>
            );
            title = 'Payment Cancelled';
            description = "No charges were made. You can close this tab and try again from FilterPixel whenever you're ready.";
        } else {
            icon = (
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(47,161,140,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <Info size={32} style={{ color: '#a3a4a5' }} />
                </div>
            );
            title = 'Return to FilterPixel';
            description = 'Please use the FilterPixel app to purchase credit packs. You can close this browser tab.';
        }

        return (
            <div style={{ width: '100%', minHeight: '100vh', background: '#0e0f13', color: '#f0f1f5', fontFamily: 'Inter, Plus Jakarta Sans, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>
                <div style={{ textAlign: 'center', maxWidth: 420 }}>
                    {icon}
                    <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>{title}</h2>
                    <p style={{ color: '#9ca3af', fontSize: 15, marginBottom: 24, lineHeight: 1.6 }}>{description}</p>
                    <div style={{ background: '#1e2029', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 24px', fontSize: 13, color: '#9ca3af' }}>
                        ✓ You can safely close this browser tab now.
                    </div>
                </div>
            </div>
        );
    }

    // ── Handlers ──

    const handleSelectPack = async () => {
        if (!email) {
            setError('No user email found. Please access this page from the app.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Snapshot current credits before checkout so we can verify increase later
            let snapshotCredits = null;
            try {
                const balRes = await fetch(`${API_BASE_URL}/v4/credits/check-balance?email=${encodeURIComponent(email)}`);
                const balData = await balRes.json();
                if (balData.success) snapshotCredits = balData.credits;
            } catch (_) { /* non-fatal */ }

            const response = await fetch(`${API_BASE_URL}/v4/credits/create-addon-checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to start checkout');
            }

            // Open Stripe Checkout in the user's default browser (not inside Electron)
            window.open(data.url, '_blank');
            setCreditsBeforeCheckout(snapshotCredits);
            setLoading(false);
            setCheckoutOpened(true);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
            setLoading(false);
        }
    };

    const handleConfirmPayment = async () => {
        setVerifying(true);
        setVerifyError('');
        try {
            const res = await fetch(`${API_BASE_URL}/v4/credits/check-balance?email=${encodeURIComponent(email)}`);
            const data = await res.json();
            if (!data.success) throw new Error('Could not verify credits.');

            const newCredits = data.shoots?.total ?? data.credits;
            // Credits/shoots increased → payment was processed
            if (creditsBeforeCheckout === null || newCredits > creditsBeforeCheckout) {
                setPaymentDone(true);
            } else {
                setVerifyError("Credits haven't arrived yet. The payment may still be processing — please wait a moment and try again.");
            }
        } catch (err) {
            setVerifyError(err.message || 'Verification failed. Please try again.');
        } finally {
            setVerifying(false);
        }
    };

    const handleTalkToSales = () => {
        window.open('mailto:support@filterpixel.com?subject=Enterprise%20DeepCull%20Plan&body=Hi%2C%20I%20need%20more%20than%205%2C500%20photos%20per%20shoot%20regularly.', '_blank');
    };

    // ── Render ──

    // ── In-app payment result overlays (after Stripe deep-link redirects back) ──

    if (payment === 'cancelled') {
        return (
            <div style={{ width: '100%', height: '100%', background: '#0e0f13', color: '#f0f1f5', fontFamily: 'Inter, Plus Jakarta Sans, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(234,179,8,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <Info size={32} style={{ color: '#facc15' }} />
                </div>
                <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Payment Cancelled</h2>
                <p style={{ color: '#9ca3af', fontSize: 15, maxWidth: 400, lineHeight: 1.6, marginBottom: 24 }}>
                    No charges were made. You can close this dialog and try again whenever you're ready.
                </p>
                <div style={{ background: '#1e2029', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 24px', fontSize: 13, color: '#9ca3af' }}>
                    ✓ You can safely close this dialog.
                </div>
            </div>
        );
    }

    if (payment === 'success') {
        return (
            <div style={{ width: '100%', height: '100%', background: '#0e0f13', color: '#f0f1f5', fontFamily: 'Inter, Plus Jakarta Sans, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(47,161,140,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <Check size={32} style={{ color: '#2fa18c' }} />
                </div>
                <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Credits Added!</h2>
                <p style={{ color: '#9ca3af', fontSize: 15, maxWidth: 400, lineHeight: 1.6, marginBottom: 24 }}>
                    Your extra DeepCull shoot has been added to your account. You can close this dialog.
                </p>
                <div style={{ background: '#1e2029', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 24px', fontSize: 13, color: '#9ca3af' }}>
                    ✓ You can safely close this dialog.
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100%', background: 'transparent', color: '#f0f1f5', fontFamily: 'Inter, Plus Jakarta Sans, sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

                .pm-scrollbar::-webkit-scrollbar { width: 6px; }
                .pm-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .pm-scrollbar::-webkit-scrollbar-thumb { background: #2a2b2e; border-radius: 3px; }
                .pm-scrollbar::-webkit-scrollbar-thumb:hover { background: #38393d; }

                @keyframes pm-spin { to { transform: rotate(360deg); } }
                .pm-spin { animation: pm-spin 0.7s linear infinite; }

                @keyframes pm-slideUp {
                    from { opacity: 0; transform: translateY(18px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .pm-slide { animation: pm-slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

                .pm-card-featured {
                    background: linear-gradient(135deg, rgba(45,212,191,0.06) 0%, #1e2029 60%);
                    border: 1px solid #2dd4bf;
                    box-shadow: 0 0 0 1px rgba(45,212,191,0.15), inset 0 1px 0 rgba(45,212,191,0.1);
                    border-radius: 16px;
                    padding: 20px;
                    position: relative;
                    overflow: hidden;
                    cursor: pointer;
                    transition: box-shadow 0.2s;
                }
                .pm-card-featured::before {
                    content: '';
                    position: absolute;
                    top: -20px; right: -20px;
                    width: 100px; height: 100px;
                    background: radial-gradient(circle, rgba(45,212,191,0.2) 0%, transparent 70%);
                    pointer-events: none;
                }
                .pm-card-featured:hover { box-shadow: 0 0 0 1px rgba(45,212,191,0.3), 0 8px 24px rgba(45,212,191,0.1); }

                .pm-card-standard {
                    background: #1e2029;
                    border: 1px solid rgba(255,255,255,0.07);
                    border-radius: 16px;
                    padding: 20px;
                    cursor: pointer;
                    transition: border-color 0.2s;
                    display: flex;
                    flex-direction: column;
                }
                .pm-card-standard:hover { border-color: rgba(255,255,255,0.18); }

                .pm-btn-primary {
                    width: 100%;
                    padding: 12px;
                    border-radius: 10px;
                    border: none;
                    background: #2dd4bf;
                    color: #0a0e0d;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-family: 'Inter', sans-serif;
                }
                .pm-btn-primary:hover:not(:disabled) { background: #5eead4; box-shadow: 0 0 20px rgba(45,212,191,0.3); }
                .pm-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

                .pm-btn-secondary {
                    width: 100%;
                    padding: 12px;
                    border-radius: 10px;
                    background: transparent;
                    color: #9ca3af;
                    border: 1px solid rgba(255,255,255,0.07);
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .pm-btn-secondary:hover { border-color: rgba(255,255,255,0.18); color: #f0f1f5; }
            `}</style>

            {/* Payment In Progress / Completed Overlay */}
            {checkoutOpened && (
                <div style={{ position: 'absolute', inset: 0, background: '#1a1b1e', zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
                    {paymentDone ? (
                        <>
                            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(47,161,140,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                                <Check size={32} style={{ color: '#2fa18c' }} />
                            </div>
                            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Credits Added!</h2>
                            <p style={{ color: '#9ca3af', fontSize: 15, maxWidth: 420, lineHeight: 1.6, marginBottom: 32 }}>
                                Your extra DeepCull shoot has been added to your account. You can close this dialog.
                            </p>
                        </>
                    ) : (
                        <>
                            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(47,161,140,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2fa18c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                                    <line x1="1" y1="10" x2="23" y2="10" />
                                </svg>
                            </div>
                            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Complete Payment in Your Browser</h2>
                            <p style={{ color: '#9ca3af', fontSize: 15, maxWidth: 420, lineHeight: 1.6, marginBottom: 24 }}>
                                A Stripe Checkout page has been opened in your browser. After completing payment, click the button below.
                            </p>
                            {verifyError && (
                                <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 20px', fontSize: 13, color: '#f87171', maxWidth: 420, marginBottom: 20, lineHeight: 1.5 }}>
                                    {verifyError}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    onClick={handleConfirmPayment}
                                    disabled={verifying}
                                    style={{ background: verifying ? '#1e6b5c' : '#2fa18c', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: verifying ? 'not-allowed' : 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8, opacity: verifying ? 0.75 : 1 }}
                                >
                                    {verifying ? (
                                        <>
                                            <svg style={{ animation: 'pm-spin 0.7s linear infinite' }} width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
                                            Verifying...
                                        </>
                                    ) : '✓ I\'ve Completed Payment'}
                                </button>
                                <button
                                    onClick={() => { setCheckoutOpened(false); setVerifyError(''); }}
                                    style={{ background: '#1e2029', color: '#d1d5db', border: '1px solid rgba(255,255,255,0.1)', padding: '12px 24px', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }}
                                >
                                    ← Go Back
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── Top Banner: Trial Used ── */}
            <div style={{ background: 'rgba(248,113,113,0.1)', borderBottom: '1px solid rgba(248,113,113,0.2)', color: '#f87171', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, flexShrink: 0 }}>
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Your free DeepCull shoots have been used
            </div>

            {/* Error Banner */}
            {error && (
                <div style={{ background: 'rgba(239,68,68,0.15)', borderBottom: '1px solid rgba(239,68,68,0.4)', color: '#f87171', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                    <X size={16} />
                    {error}
                    <button onClick={() => setError('')} style={{ marginLeft: 12, opacity: 0.7, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex' }}>
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* ── Scrollable Content ── */}
            <div className="pm-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '28px 28px 0' }}>

                {/* Header */}
                <div style={{ marginBottom: 24 }}>
                    {/* Trial Used Pill */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)',
                        borderRadius: 100, padding: '4px 10px', fontSize: 12, color: '#f87171',
                        fontWeight: 600, letterSpacing: '0.02em', marginBottom: 14,
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f87171', display: 'inline-block' }} />
                        Free shoots used
                    </div>

                    <h1 style={{ fontSize: 26, fontWeight: 800, color: '#f0f1f5', lineHeight: 1.2, marginBottom: 8, fontFamily: 'Inter, sans-serif' }}>
                        Need one more shoot?
                    </h1>
                    <p style={{ fontSize: 14, color: '#9ca3af', lineHeight: 1.6, margin: 0 }}>
                        Add an extra DeepCull shoot for $7.99. Up to 5,500 photos per shoot. All genre modes included. Never expires.
                    </p>
                </div>

                {/* ── Cards ── */}
                <div className="pm-slide" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>

                    {/* Extra Shoot Card */}
                    <div className="pm-card-featured" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2dd4bf', marginBottom: 12 }}>Extra Shoot</div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16, flex: 1 }}>
                            <div style={{ fontSize: 32, fontWeight: 800, color: '#f0f1f5', marginBottom: 4 }}>$7.99</div>
                            <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>
                                One DeepCull shoot — up to 5,500 photos. All genre modes. Never expires.
                            </div>
                        </div>

                        {/* Coverage Box */}
                        <div style={{ background: 'rgba(45,212,191,0.08)', border: '1px solid rgba(45,212,191,0.15)', borderRadius: 8, padding: '9px 11px', display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#2dd4bf" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                            </svg>
                            <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>
                                <strong style={{ color: '#f0f1f5', fontWeight: 500 }}>Up to 5,500 photos</strong> per shoot — one-time purchase, never expires
                            </div>
                        </div>

                        {/* Feature List */}
                        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {['Genre-specific AI culling', 'Score + Reason on every photo', 'All genre modes included'].map((feat) => (
                                <li key={feat} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: '#9ca3af' }}>
                                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="#2dd4bf" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    {feat}
                                </li>
                            ))}
                        </ul>

                        {/* CTA */}
                        <button className="pm-btn-primary" onClick={handleSelectPack} disabled={loading}>
                            {loading ? (
                                <>
                                    <svg className="pm-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                                    </svg>
                                    Redirecting...
                                </>
                            ) : 'Buy Extra Shoot — $7.99'}
                        </button>
                    </div>

                    {/* Enterprise Card */}
                    <div className="pm-card-standard">
                        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 10 }}>Enterprise</div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: '#f0f1f5', marginBottom: 8, marginTop: 4, fontFamily: 'Inter, sans-serif' }}>Custom</div>
                        <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5, marginBottom: 16, flex: 1 }}>
                            Shooting more than 5,500 photos per shoot regularly, or need multi-seat access?
                        </div>

                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 20, flex: 1 }}>
                            <li style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#9ca3af' }}>
                                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0 }}>
                                    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
                                </svg>
                                Volume shoot discounts
                            </li>
                            <li style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#9ca3af' }}>
                                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0 }}>
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                Multi-seat team accounts
                            </li>
                            <li style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#9ca3af' }}>
                                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0 }}>
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13" />
                                </svg>
                                Priority support &amp; SLA
                            </li>
                        </ul>

                        <button className="pm-btn-secondary" onClick={handleTalkToSales}>Talk to Sales</button>
                    </div>

                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '0 0 16px' }} />

                {/* Footer Note */}
                <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', lineHeight: 1.6, paddingBottom: 24 }}>
                    Shoots never expire · All genre modes included · Secure payment via Stripe
                    {/* <a href="#" style={{ color: '#2dd4bf', textDecoration: 'none' }}>See full pricing details</a> */}
                </p>

            </div>
        </div>
    );
};

// --- PREVIEW CONTAINER (For Context) ---
const App = () => {
    // Simulating the "App Popup" environment
    return (
        <div className="h-screen w-full bg-black flex items-center justify-center p-4">
            {/* Limit the container size to simulate a popup */}
            <div className="w-full max-w-5xl h-[80vh] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl relative">
                <PricingModal />
            </div>
        </div>
    );
};

export default App;